import { useEffect } from 'react';
import { Video, Loader2 } from 'lucide-react';
import { useMeetingRecordingStore } from '../../lib/stores/meetingRecordingStore';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { MeetingRecordingCard } from './MeetingRecordingCard';
import { supabase } from '../../lib/supabase';

interface MeetingRecordingsListProps {
  dealId?: string;
  leadId?: string;
  clientId?: string;
}

export function MeetingRecordingsList({ dealId, leadId, clientId }: MeetingRecordingsListProps) {
  const { canUseAIMeetingBot } = usePermissions();
  const {
    recordings,
    recordingsLoading,
    recordingsError,
    fetchRecordingsForDeal,
    fetchRecordingsForLead,
    fetchRecordingsForClient,
  } = useMeetingRecordingStore();

  // Fetch recordings on mount
  useEffect(() => {
    if (!canUseAIMeetingBot) return;
    if (dealId) fetchRecordingsForDeal(dealId);
    else if (leadId) fetchRecordingsForLead(leadId);
    else if (clientId) fetchRecordingsForClient(clientId);
  }, [dealId, leadId, clientId, canUseAIMeetingBot]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!canUseAIMeetingBot) return;
    const entityId = dealId || leadId || clientId;
    if (!entityId) return;

    const filterColumn = dealId ? 'deal_id' : leadId ? 'lead_id' : 'client_id';

    const subscription = supabase
      .channel(`meeting-recordings-${entityId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'meeting_recordings',
        filter: `${filterColumn}=eq.${entityId}`,
      }, () => {
        // Refresh on any change
        if (dealId) fetchRecordingsForDeal(dealId);
        else if (leadId) fetchRecordingsForLead(leadId);
        else if (clientId) fetchRecordingsForClient(clientId);
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [dealId, leadId, clientId, canUseAIMeetingBot]);

  // Hide if no permission (after hooks to respect Rules of Hooks)
  if (!canUseAIMeetingBot) return null;

  if (recordingsLoading && recordings.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (recordingsError) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-red-500">{recordingsError}</p>
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="text-center py-12">
        <Video className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Nessuna registrazione</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Usa il pulsante "Registra" per avviare una registrazione meeting
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recordings.map((recording) => (
        <MeetingRecordingCard key={recording.id} recording={recording} />
      ))}
    </div>
  );
}
